/**
 * Citing while writing: type `\cite{` (or `[@`) and choose a paper from the project's library by
 * author, title, year or key. The citation is stored as `[@key](bl://cite/<id>)`, which the
 * reference list, the checks and the LaTeX/Word export understand. A DOI that is not in the
 * library yet can be added and cited in one step.
 */
import { autocompletion, type Completion, type CompletionContext, type CompletionResult } from "@codemirror/autocomplete";
import type { Extension } from "@codemirror/state";
import type { EditorView } from "@codemirror/view";

export interface CiteItem {
  id: string;
  key: string;
  title: string;
  authors: string;
  year: number | null;
}

export interface CiteSource {
  items(): CiteItem[];
  /** Changes when the library changes (citations are redrawn). */
  version?: number;
  /** Add a paper by DOI and return it (or null when it could not be added). */
  addDoi?(doi: string): Promise<CiteItem | null>;
}

const TRIGGER = /(\\cite[pt]?\{|\[@)([^\]}\n]{0,80})$/;
const DOI = /^10\.\d{4,9}\/\S+$/;

export const citationText = (item: Pick<CiteItem, "id" | "key">) => `[@${item.key}](bl://cite/${item.id})`;

function matches(item: CiteItem, words: string[]) {
  const hay = `${item.key} ${item.authors} ${item.title} ${item.year ?? ""}`.toLowerCase();
  return words.every((w) => hay.includes(w));
}

function source(src: { current: CiteSource | null | undefined }) {
  return (ctx: CompletionContext): CompletionResult | null => {
    const s = src.current;
    if (!s) return null;
    const m = ctx.matchBefore(TRIGGER);
    if (!m) return null;
    const trigger = TRIGGER.exec(m.text)!;
    const query = trigger[2].trim();
    const words = query.toLowerCase().split(/[\s,;]+/).filter(Boolean);
    const all = s.items();
    const found = (words.length ? all.filter((i) => matches(i, words)) : all).slice(0, 30);
    const options: Completion[] = found.map((item, i) => ({
      label: `${item.authors || "Unknown author"} ${item.year ?? "n.d."}`,
      detail: item.title.length > 70 ? `${item.title.slice(0, 70)}…` : item.title,
      type: "text",
      boost: -i,
      apply: (view: EditorView, _c: Completion, from: number, to: number) => {
        // Also take a closing brace typed after the query: \cite{smith} → one citation.
        const end = view.state.sliceDoc(to, to + 1) === (trigger[1] === "[@" ? "]" : "}") ? to + 1 : to;
        const text = citationText(item);
        view.dispatch({ changes: { from, to: end, insert: text }, selection: { anchor: from + text.length } });
      },
    }));
    const doi = query.replace(/^https?:\/\/(dx\.)?doi\.org\//i, "").replace(/^doi:\s*/i, "");
    if (s.addDoi && DOI.test(doi) && !all.some((i) => i.key.toLowerCase() === doi.toLowerCase())) {
      options.push({
        label: `Add ${doi} to the library and cite it`,
        type: "keyword",
        apply: (view: EditorView, _c: Completion, from: number, to: number) => {
          const marker = `⟨adding ${doi}…⟩`;
          view.dispatch({ changes: { from, to, insert: marker }, selection: { anchor: from + marker.length } });
          void s.addDoi!(doi).then((item) => {
            const at = view.state.doc.toString().indexOf(marker);
            if (at < 0) return;
            view.dispatch({ changes: { from: at, to: at + marker.length, insert: item ? citationText(item) : `[citation needed: ${doi}]` } });
          });
        },
      });
    }
    if (!options.length)
      options.push({ label: words.length ? "No paper in this library matches" : "The library is empty", detail: "Add papers in Papers & library, or type a DOI", type: "text", apply: () => undefined });
    return { from: m.from, options, filter: false };
  };
}

export function citeCompletion(src: { current: CiteSource | null | undefined }): Extension {
  // The search is instant: no typing delay, so Enter right after typing picks the paper (not a new line).
  // The list opens only after a deliberate \cite{, so Enter may pick a paper at once.
  return autocompletion({ override: [source(src)], activateOnTyping: true, activateOnTypingDelay: 0, interactionDelay: 0, icons: false, maxRenderedOptions: 30 });
}
